import { useState } from 'react';
import snorlaxWebp from '../assets/deco/snorlax.webp';
import ranuraWebp from '../assets/deco/ranura.webp';

/**
 * Decoracion tematica del sitio: pokebola, siluetas y la mascota, siempre
 * sobrias.
 *
 * REGLAS DE LA CASA. Los trazos (pokebola, candado) son vector propio
 * monocromo con `currentColor`, para heredar el gris del contexto. La mascota
 * de los vacios es una ilustracion generada por el dueño del proyecto
 * (/deco/snorlax.webp, paleta de la casa, fondo transparente); el SVG
 * geometrico de abajo queda como respaldo si la imagen no carga. Aparece solo
 * donde no estorba a los datos: estados vacios, cargas y el fondo lejano.
 */

/** Pokebola en trazo fino. El icono de la casa: cargas, vacios, fondo. */
export function Pokebola({ size = 56, className = '' }) {
  return (
    <svg
      viewBox="0 0 100 100"
      width={size}
      height={size}
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <g stroke="currentColor" strokeWidth="3">
        <circle cx="50" cy="50" r="45" />
        <path d="M5 50h31M64 50h31" strokeLinecap="round" />
        <circle cx="50" cy="50" r="14" />
        <circle cx="50" cy="50" r="5.5" fill="currentColor" stroke="none" />
      </g>
    </svg>
  );
}

/**
 * Snorlax dormido, en dos tonos del mismo color. Formas geometricas simples
 * -- orejas, cabeza, panza -- que se leen como silueta sin pretender ser un
 * dibujo: es la mascota de los estados "aqui no hay nada todavia".
 */
export function SnorlaxDormido({ size = 96, className = '' }) {
  return (
    <svg
      viewBox="0 0 132 96"
      width={size}
      height={(size * 96) / 132}
      aria-hidden="true"
      className={className}
    >
      <g fill="currentColor">
        {/* Orejas */}
        <path d="M44 22 35 5l20 8Z" opacity=".55" />
        <path d="M88 22 97 5 77 13Z" opacity=".55" />
        {/* Cabeza */}
        <ellipse cx="66" cy="31" rx="26" ry="20" opacity=".55" />
        {/* Cuerpo */}
        <ellipse cx="66" cy="68" rx="44" ry="27" opacity=".55" />
        {/* Panza, un punto mas clara */}
        <ellipse cx="66" cy="70" rx="30" ry="19" opacity=".28" />
        {/* Ojos cerrados */}
        <path
          d="M55 30q4 3.5 8 0M71 30q4 3.5 8 0"
          stroke="currentColor"
          strokeWidth="2.4"
          strokeLinecap="round"
          fill="none"
          opacity=".9"
        />
      </g>
      {/* El sueño. Letras dibujadas a trazo para no depender de tipografias. */}
      <g stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" fill="none">
        <path d="M103 26h9l-9 11h9" opacity=".8" />
        <path d="M117 8h7l-7 8.5h7" opacity=".55" />
      </g>
    </svg>
  );
}

/**
 * Estado vacio con mascota. El texto manda; la silueta ambienta.
 *
 *   tipo="pokebola"  no se encontro nada (busquedas, errores suaves)
 *   tipo="snorlax"   aun no hay contenido propio (partidas)
 *   tipo="ranura"    aun no hay MAZOS: una caja de mazo abierta y vacia
 *
 * POR QUE LOS MAZOS TIENEN SU PROPIA ILUSTRACION. El Snorlax dormido dice "no
 * hay nada todavia", que es cierto pero vale igual para cualquier pantalla. La
 * caja de mazo abierta dice ademas QUE es lo que falta y donde iria: es una
 * ranura esperando, o sea una invitacion, que es lo que la casa pide de un
 * estado vacio. Snorlax se queda en Partidas, donde no hay un objeto
 * equivalente que ensenar.
 */
export function Vacio({ tipo = 'pokebola', titulo, texto, children, className = '' }) {
  /* Si la ilustracion no llega (404, red), cae al SVG geometrico: un vacio
     sin mascota se leeria como un fallo, que es justo lo que no debe parecer. */
  const [sinImagen, setSinImagen] = useState(false);
  /* Importadas y no escritas a mano: asi Vite les pone huella de contenido en
     el nombre y un cambio de arte no se queda atrapado una semana en la cache
     del navegador (los estaticos van con max-age de 7 dias). */
  const ILUSTRADOS = { snorlax: snorlaxWebp, ranura: ranuraWebp };
  return (
    <div className={`vacio ${className}`.trim()}>
      <span className={`vacio__arte vacio__arte--${tipo}`} aria-hidden="true">
        {ILUSTRADOS[tipo] ? (
          sinImagen ? (
            <SnorlaxDormido size={104} />
          ) : (
            <img
              src={ILUSTRADOS[tipo]}
              width={124}
              height={124}
              alt=""
              loading="lazy"
              decoding="async"
              className="vacio__img"
              onError={() => setSinImagen(true)}
            />
          )
        ) : (
          <Pokebola size={54} />
        )}
      </span>
      {titulo && <p className="vacio__titulo">{titulo}</p>}
      {texto && <p className="muted vacio__texto">{texto}</p>}
      {children}
    </div>
  );
}

/**
 * NO SE PUDO CARGAR. El tercer estado, y el que faltaba.
 *
 * La casa distinguia carga (pokebola meciendose) de vacio (Snorlax dormido),
 * pero un fallo de red caia en el vacio: si la API no contestaba, el meta
 * decia "aun no hay datos de torneos" -- una frase sobre el CONTENIDO -- y
 * quien la leia se iba pensando que el sitio estaba sin datos, cuando lo que
 * pasaba era que no se habia podido preguntar. Un vacio es una respuesta; un
 * fallo es la ausencia de respuesta, y se arregla reintentando.
 *
 * La pokebola va PARTIDA -- las dos mitades separadas, con la raya rota -- y
 * en el rojo de estado de la casa. Es la misma pieza de siempre, que es lo que
 * hace que se lea como "esto no salio bien" y no como un icono nuevo que hay
 * que aprender.
 */
export function Fallo({ titulo, texto, onReintentar, textoReintentar, className = '' }) {
  return (
    <div className={`vacio vacio--fallo ${className}`.trim()} role="alert">
      <span className="vacio__arte" aria-hidden="true">
        <svg viewBox="0 0 100 100" width={54} height={54} fill="none">
          <g stroke="currentColor" strokeWidth="3" strokeLinecap="round">
            {/* Mitad de arriba, corrida a la izquierda; la de abajo, a la
                derecha: el corte cuenta la averia sin dibujar una grieta. */}
            <g transform="translate(-5 -3)">
              <path d="M5 50a45 45 0 0 1 90 0" />
              <path d="M5 50h31M64 50h31" />
            </g>
            <g transform="translate(5 3)">
              <path d="M95 50a45 45 0 0 1-90 0" />
              <path d="M5 50h31M64 50h31" />
            </g>
            <circle cx="50" cy="50" r="14" opacity=".45" />
          </g>
        </svg>
      </span>
      {titulo && <p className="vacio__titulo">{titulo}</p>}
      {texto && <p className="muted vacio__texto">{texto}</p>}
      {onReintentar && (
        <button type="button" className="btn" onClick={onReintentar}>
          {textoReintentar}
        </button>
      )}
    </div>
  );
}

/** Candado en trazo de la casa, para los avisos de Pro (antes era un emoji). */
export function Candado({ size = 22, className = '' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      aria-hidden="true"
      className={className}
    >
      <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10" />
        <rect x="5" y="10" width="14" height="10.5" rx="2.5" />
        <path d="M12 14.5v2" />
      </g>
    </svg>
  );
}

/**
 * Carga con la pokebola meciendose, como cuando algo esta a punto de caer.
 * Sustituye a los "Cargando…" de texto suelto: mismo mensaje, mas casa.
 */
export function Cargando({ texto, className = '' }) {
  return (
    <p className={`cargando ${className}`.trim()} role="status">
      <Pokebola size={20} className="cargando__bola" />
      {texto && <span className="muted">{texto}</span>}
    </p>
  );
}
